import { Switch as HeadlessSwitch } from '@headlessui/react';
import { FC, useContext, useMemo } from 'react';

import { clstx } from '@payaca/utilities/cvaMerge';
import { FieldContext } from '../Field/Context';

export enum SwitchSizeVariant {
  SM = 'sm',
  MD = 'md',
  LG = 'lg',
}

export type SwitchProps = {
  className?: string;
  onChange?: (value: boolean) => void;
  value?: boolean;
  disabled?: boolean;
  sizeVariant?: SwitchSizeVariant;
  /** Accessible name, for use outside a labelled `Field`. */
  'aria-label'?: string;
};

const Switch: FC<SwitchProps> = ({
  className,
  onChange,
  value,
  disabled,
  sizeVariant = SwitchSizeVariant.MD,
  'aria-label': ariaLabel,
}) => {
  const { id, name, validationState } = useContext(FieldContext);

  const { bgColour, toggleBgColour } = useMemo(() => {
    if (validationState && !validationState.isValid) {
      return { bgColour: 'bg-red-200', toggleBgColour: 'bg-red-500' };
    } else if (value) {
      return {
        bgColour: 'bg-none bg-purple-300',
        toggleBgColour: 'bg-purple-600',
      };
    } else {
      return { bgColour: 'bg-gray-100', toggleBgColour: 'bg-white' };
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'validationState'
  }, [validationState?.isValid, value]);

  const { sizeClasses, toggleSizeClasses, checkedClasses } = useMemo(() => {
    switch (sizeVariant) {
      case SwitchSizeVariant.SM:
        return {
          sizeClasses: 'w-11 h-6',
          toggleSizeClasses: 'w-5 h-5',
          checkedClasses: 'translate-x-5',
        };

      case SwitchSizeVariant.MD:
        return {
          sizeClasses: 'w-14 h-7',
          toggleSizeClasses: 'w-6 h-6',
          checkedClasses: 'translate-x-7',
        };

      case SwitchSizeVariant.LG:
        return {
          // oxlint-disable-next-line payaca/no-tailwind-arbitrary-values
          sizeClasses: 'w-[5.67rem] h-9',
          toggleSizeClasses: 'w-8 h-8',
          checkedClasses: 'translate-x-9',
        };
    }
  }, [sizeVariant]);

  return (
    <HeadlessSwitch
      id={id}
      name={name}
      aria-label={ariaLabel}
      checked={value}
      onChange={onChange}
      disabled={!!disabled}
      className={clstx(
        `relative text-left p-0 cursor-pointer appearance-none rounded-full border-2 border-transparent ring-1 ring-transparent ring-offset-white focus:outline-none`,
        'focus-visible:ring-2 focus-visible:ring-purple-600 focus-visible:ring-offset-1',
        'transition-colors duration-200 ease-in-out',
        sizeClasses,
        bgColour,
        className
      )}
    >
      {({ checked }) => {
        return (
          <span
            className={clstx(
              `inline-block rounded-full shadow ring-0 bg-white`,
              'transform transition duration-200 ease-in-out',
              checked ? checkedClasses : 'translate-x-0',
              toggleBgColour,
              toggleSizeClasses
            )}
          />
        );
      }}
    </HeadlessSwitch>
  );
};

export default Switch;
